import { Theme } from '@mui/material/styles';
import { axisClasses } from '@mui/x-charts/ChartsAxis';
import { chartsGridClasses } from '@mui/x-charts/ChartsGrid';

/**
 * Chart container styles
 */
export const getChartContainerStyles = (theme: Theme) => ({
  flexDirection: 'column' as const,
  height: '100%',
  display: 'flex',
  flex: 1,
  padding: 0, // Remove all padding to let chart extend to card edges
  backgroundColor: theme.palette.background.contrast,
  position: 'relative' as const,
  // Global tooltip styling injection
  '& *[data-popper-placement]': {
    backgroundColor: `${theme.palette.background.default} !important`,
    color: `${theme.palette.text.primary} !important`,
    '& *': {
      color: `${theme.palette.text.primary} !important`,
    },
  },
});

/**
 * Chart component styles
 */
export const getChartStyles = (theme: Theme) => ({
  width: '100%',
  height: '100%',
  flexGrow: 1,
  // Global tooltip text color override
  '& [role="tooltip"]': {
    backgroundColor: `${theme.palette.background.default} !important`,
    color: `${theme.palette.text.primary} !important`,
    borderRadius: theme.spacing(2),
    border: `1px solid ${theme.palette.text.fifth} !important`,
    '& *': {
      color: `${theme.palette.text.primary} !important`,
    },
  },
  // Additional global styles for MUI Charts tooltips
  '& + div[role="tooltip"]': {
    backgroundColor: `${theme.palette.background.default} !important`,
    color: `${theme.palette.text.primary} !important`,
    '& *': {
      color: `${theme.palette.text.primary} !important`,
    },
  },
  [`& .MuiAreaElement-series-balance`]: {
    fill: 'url(#balance) !important',
  },
  [`& .MuiLineElement-series-balance`]: {
    strokeWidth: '2px !important',
  },
  // Fallback selectors
  [`& path[class*="MuiAreaElement"]`]: {
    fill: 'url(#balance) !important',
  },
  [`& path[class*="MuiLineElement"]`]: {
    strokeWidth: '2px !important',
  },
  [`& area`]: {
    fill: 'url(#balance) !important',
  },
  [`& .${chartsGridClasses.line}`]: {
    stroke: theme.palette.text.fifth,
  },
  // Hide legend
  [`& .MuiChartsLegend-root`]: {
    display: 'none !important',
  },
  // Tooltip styles - comprehensive approach
  [`& .MuiChartsTooltip-root`]: {
    backgroundColor: `${theme.palette.background.default} !important`,
    borderRadius: theme.spacing(2),
    border: `1px solid ${theme.palette.text.fifth} !important`,
    color: `${theme.palette.text.primary} !important`,
    '& *': {
      color: `${theme.palette.text.primary} !important`,
    },
  },
  [`& .MuiPopper-root`]: {
    '& .MuiChartsTooltip-paper': {
      backgroundColor: `${theme.palette.background.default} !important`,
      borderRadius: theme.spacing(2),
      border: `1px solid ${theme.palette.text.fifth} !important`,
      color: `${theme.palette.text.primary} !important`,
    },
    '& .MuiChartsTooltip-root': {
      backgroundColor: `${theme.palette.background.default} !important`,
      color: `${theme.palette.text.primary} !important`,
    },
    '& *': {
      color: `${theme.palette.text.primary} !important`,
      backgroundColor: 'transparent !important',
    },
  },
  // Global tooltip override
  [`& .MuiTooltip-tooltip`]: {
    backgroundColor: `${theme.palette.background.default} !important`,
    color: `${theme.palette.text.primary} !important`,
    borderRadius: theme.spacing(2),
    border: `1px solid ${theme.palette.text.fifth} !important`,
  },
  // Legend text color fix
  [`& .MuiChartsTooltip-labelCell`]: {
    color: `${theme.palette.text.primary} !important`,
  },
  [`.${axisClasses.root}`]: {
    [`.${axisClasses.tickLabel}`]: {
      fill: theme.palette.text.third,
      // Ensure y-axis labels don't get truncated
      overflow: 'visible !important',
      whiteSpace: 'nowrap !important',
      textOverflow: 'unset !important',
    },
  },
});

/**
 * Tooltip styles
 */
export const getTooltipStyles = (theme: Theme) => ({
  '& .MuiChartsTooltip-root': {
    backgroundColor: `${theme.palette.background.default} !important`,
    borderRadius: theme.spacing(2),
    border: `1px solid ${theme.palette.text.fifth}`,
    color: `${theme.palette.text.primary} !important`,
  },
  '& .MuiChartsTooltip-table': {
    backgroundColor: `${theme.palette.background.default} !important`,
    color: `${theme.palette.text.primary} !important`,
  },
  '& .MuiChartsTooltip-paper': {
    backgroundColor: `${theme.palette.background.default} !important`,
    borderRadius: theme.spacing(2),
    border: `1px solid ${theme.palette.text.fifth}`,
    color: `${theme.palette.text.primary} !important`,
  },
  backgroundColor: `${theme.palette.background.default} !important`,
  borderRadius: theme.spacing(2),
  border: `1px solid ${theme.palette.text.fifth}`,
  color: `${theme.palette.text.primary} !important`,
});
